import React, { useCallback, useEffect, useState } from 'react';
import { DNABox, DNADivider, GridList, Iffy } from '@alucio/lux-ui';

import {
  DNAEmpty,
  DNAFolderDocumentRow,
  DNAFolderItemRow,
  DNAFolderListRow,
  EmptyVariant,
  GridListFolderHeaderRow,
} from 'src/components/DNA';
import { DocumentORM, FolderItemORM, FolderORM, isDocumentORM, isFolderItemORM, isFolderORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { sortCollection } from 'src/state/redux/selector/common';
import { sorts as favoriteSorts } from 'src/state/redux/favorites/query'
import { SortDir } from '../hooks/useSort';
import {
  COLUMN_CONFIG,
  FolderHeaderType,
  FolderVariants,
  GRID_LIST_STYLES_VARIANTS_CONFIGS_UI2,
} from 'src/screens/MyFolders/constants';
import loggers from 'src/utils/logger';

export interface DNAFolderGridListProps {
  variant: FolderVariants;
  items: (FolderORM | FolderItemORM | DocumentORM)[];
  emptyVariant: EmptyVariant;
  onPress: (selectedItemORM: FolderItemORM | FolderORM | DocumentORM) => void
}

// [TODO-REFACTOR] - Can't this be done through a selector
const useFolderSort = (variant: FolderVariants, items: (FolderORM | FolderItemORM | DocumentORM)[]) => {
  const [sortDir, setSortDir] = useState<SortDir>(
    variant === FolderVariants.FAVORITES
      ? SortDir.desc
      : SortDir.asc,
  );
  const [sortKey, setSortKey] = useState<FolderHeaderType>(
    variant === FolderVariants.MY_UPLOADS
      ? FolderHeaderType.UPLOADED
      : variant === FolderVariants.FAVORITES
        ? FolderHeaderType.UPDATED
        : FolderHeaderType.NAME,
  );
  const [sortedItems, setSortedItems] = useState<(FolderORM | FolderItemORM | DocumentORM)[]>(items);

  const toggleSort = useCallback((key: FolderHeaderType) => {
    if (sortKey === key) {
      setSortDir(prev => prev === SortDir.asc ? SortDir.desc : SortDir.asc);
    } else {
      setSortKey(key);
      setSortDir(SortDir.asc);
    }
  }, [sortKey]);

  useEffect(
    () => {
      const dir = sortDir === SortDir.none ? undefined : sortDir
      if (sortKey === FolderHeaderType.UPLOADED) {
        setSortedItems(sortCollection(items, favoriteSorts.sortByFavoritedCreatedAt(dir)));
        return;
      }

      if (sortKey === FolderHeaderType.UPDATED) {
        setSortedItems(sortCollection(items, favoriteSorts.sortByFavoritedUpdatedAt(dir)));
        return;
      }

      if (sortKey === FolderHeaderType.NAME) {
        setSortedItems(sortCollection(items, favoriteSorts.sortByFavoritedTitle(dir)));
        return;
      }

      setSortedItems(items);
    },
    [sortDir, sortKey, items],
  );

  return { sortedItems, toggleSort, sortDir, sortKey };
};

const DNAFolderGridList: React.FC<DNAFolderGridListProps> = (props) => {
  const { variant, items, emptyVariant, onPress } = props;
  const { deviceMode } = useAppSettings();
  const { sortedItems, toggleSort, sortDir, sortKey } = useFolderSort(variant, items);

  const columnConfig = deviceMode === 'desktop'
    ? COLUMN_CONFIG[variant].columnConfigDesktop
    : COLUMN_CONFIG[variant].columnConfigTablet;

  return (
    <DNABox style={GRID_LIST_STYLES_VARIANTS_CONFIGS_UI2[deviceMode]}>
      <GridList cols={columnConfig} gap="8px">
        <Iffy is={!sortedItems.length}>
          <DNAEmpty emptyVariant={emptyVariant} />
        </Iffy>
        <Iffy is={sortedItems.length}>
          <>
            <GridListFolderHeaderRow
              variant={variant}
              toggleSort={toggleSort}
              sortDir={sortDir}
              sortKey={sortKey}
            />
            {sortedItems.map(item => {
              if (isFolderORM(item)) {
                return (
                  <React.Fragment key={item.model.id}>
                    <DNAFolderListRow
                      folderORM={item}
                      variant={variant}
                      onPress={onPress}
                    />
                    <DNADivider variant="secondary" />
                  </React.Fragment>
                )
              }
              else if (isFolderItemORM(item) && isFolderORM(item.relations.itemORM)) {
                return (
                  <React.Fragment key={item.relations.itemORM.model.id}>
                    <DNAFolderListRow
                      folderORM={item.relations.itemORM}
                      variant={variant}
                      onPress={onPress}
                    />
                    <DNADivider variant="secondary" />
                  </React.Fragment>
                )
              }
              else if (isDocumentORM(item)) {
                return (
                  <React.Fragment key={item.model.id}>
                    <DNAFolderDocumentRow
                      key={item.model.id}
                      documentORM={item}
                      variant={variant}
                      onPress={onPress}
                    />
                    <DNADivider variant="secondary" />
                  </React.Fragment>
                )
              }
              else if (isFolderItemORM(item)) {
                return (
                  <React.Fragment key={item.model.id}>
                    <DNAFolderItemRow
                      key={item.model.id}
                      folderItemORM={item}
                      variant={variant}
                      onPress={onPress}
                    />
                    <DNADivider variant="secondary" />
                  </React.Fragment>
                )
              }
              else {
                loggers.util.warn('Unsupported ORM type in FolderGridList', item)
                return null;
              }
            })}
          </>
        </Iffy>
      </GridList>
    </DNABox>
  )
}

export default DNAFolderGridList;
